import { FC, useState, useReducer, useEffect, useRef } from 'react'
import { Grid, TextField, Button, Box } from '@mui/material'
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone'

import { Charges } from '../../../services'
import {
  getOtherChargesSacCodeOrDefault,
  setOtherChargesSacCode,
} from '../../../utils/session.manager'
import CustomNumberField from '../Users/profile/CustomNumberField'

const DEFAULT_SAC_CODE = '995457'

type AddOtherChargesFields = 'amount' | 'chargeTitle' | 'hsnCode' | 'quantity' | 'rate'
type AddOtherChargesState = Record<
  AddOtherChargesFields,
  Partial<{
    value: string
    error: boolean
    helperText: string
  }>
>

type AddOtherChargesError = Record<
  AddOtherChargesFields,
  Record<string, string>
>

const addOtherChargesError: AddOtherChargesError = {
  amount: {
    empty: 'Please some amount',
  },
  quantity: {
    empty: 'Please enter weight',
  },
  rate: {
    empty: 'Please enter rate',
  },
  chargeTitle: {
    empty: 'Please enter charge title',
  },
  hsnCode: {
    empty: 'Please enter HSN Code',
  },
}

const initialAddOtherChargesState: AddOtherChargesState = {
  amount: {
    error: false,
    helperText: '',
  },
  chargeTitle: {
    error: false,
    helperText: '',
  },
  hsnCode: {
    error: false,
    helperText: '',
  },
  quantity: {
    error: false,
    helperText: '',
  },
  rate: {
    error: false,
    helperText: '',
  },
}
const addOtherChargesReducers = (
  state: Partial<AddOtherChargesState>,
  action
) => {
  const {
    type,
    payload: { name, value },
    helperText,
  } = action
  switch (type) {
    case 'SET':
      return {
        ...state,
        [name]: {
          ...state[name],
          error: false,
          value: value,
          helperText: helperText
            ? helperText
            : initialAddOtherChargesState[name]?.helperText,
        },
      }
    case 'ERROR':
      return {
        ...state,
        [name]: {
          ...state[name],
          error: true,
          value: value,
          helperText: addOtherChargesError[name]['empty'],
        },
      }

    case 'RESET':
      return {
        ...initialAddOtherChargesState,
        [name]: {
          ...state[name],
          error: false,
          value: value,
          helperText: initialAddOtherChargesState[name]?.helperText,
        },
      }
    default:
      return state
  }
}

type AddOtherChargesProps = {
  dummyCharge?: Charges
  addOtherCharge: (item: Charges) => void
  isGSTEnabled?: boolean // Add isGSTEnabled prop instead of bill object
  enableWeightFields?: boolean
  singleRow?: boolean
}


const AddOtherCharges: FC<AddOtherChargesProps> = ({
  dummyCharge,
  addOtherCharge,
  isGSTEnabled,
  enableWeightFields = false,
  singleRow = false,
}) => {
  const [charge, setCharge] = useState<Charges | null>(() => ({
    amount: 0,
    chargeTitle: '',
    hsnCode: getOtherChargesSacCodeOrDefault(DEFAULT_SAC_CODE),
  }))
  const inputRef = useRef<HTMLInputElement>(null)
  const [addOtherChargesData, dispatch] = useReducer(
    addOtherChargesReducers,
    initialAddOtherChargesState
  )

  useEffect(() => {
    if (!isGSTEnabled) return
    const sacCode = getOtherChargesSacCodeOrDefault(DEFAULT_SAC_CODE)
    setCharge((prev) => (prev ? { ...prev, hsnCode: sacCode } : prev))
    dispatch({ type: 'SET', payload: { name: 'hsnCode', value: sacCode } })
  }, [isGSTEnabled])




const handleKeyUp = (event) => {
  if (event.keyCode === 13) {
    // Enter key
    adOtherChargesHandler()
    event.preventDefault()
    event.stopPropagation()
  }
}

  // Sync charge title from parent; keep cached SAC code (independent of charge type).
  useEffect(() => {
    const sacCode = getOtherChargesSacCodeOrDefault(DEFAULT_SAC_CODE)
    const defaultCharge: Charges = { amount: 0, chargeTitle: '', hsnCode: sacCode }
    setCharge((prev) =>
      dummyCharge != null
        ? {
            ...defaultCharge,
            ...prev,
            ...dummyCharge,
            hsnCode: prev?.hsnCode || sacCode,
          }
        : { ...defaultCharge, ...prev, chargeTitle: '', amount: 0, hsnCode: prev?.hsnCode || sacCode }
    )
  }, [dummyCharge])

  const adOtherChargesHandler = () => {
    let error = false

    if (!charge?.chargeTitle) {
      error = true
      dispatch({ type: 'ERROR', payload: { name: 'chargeTitle', value: null } })
    }
    if (!enableWeightFields && !charge?.amount && charge?.amount !== 0) {
      error = true
      dispatch({ type: 'ERROR', payload: { name: 'amount', value: null } })
    }
    if (isGSTEnabled && !charge?.hsnCode) {
      error = true
      dispatch({ type: 'ERROR', payload: { name: 'hsnCode', value: null } })
    }
    if (enableWeightFields) {
      if (!charge?.quantity && charge?.quantity !== 0) {
        error = true
        dispatch({ type: 'ERROR', payload: { name: 'quantity', value: null } })
      }
      if (!charge?.rate && charge?.rate !== 0) {
        error = true
        dispatch({ type: 'ERROR', payload: { name: 'rate', value: null } })
      }
    }
    if (!error) {
      if (inputRef.current) {
        inputRef.current.focus() // Set focus to the text field
      }
      const chargeToAdd = enableWeightFields
        ? { ...charge, amount: charge?.amount ?? 0, quantity: Number(charge?.quantity) }
        : charge
      if (charge?.hsnCode) {
        setOtherChargesSacCode(charge.hsnCode)
      }
      addOtherCharge(chargeToAdd)
      const cachedSac = getOtherChargesSacCodeOrDefault(DEFAULT_SAC_CODE)
      setCharge({
        amount: null,
        chargeTitle: null,
        hsnCode: cachedSac,
        quantity: null,
        rate: null,
      })
    }
  }
  const onInputChange = (e) => {
    const { name, value } = e.target
    setCharge((charge) => {
      return {
        ...charge,
        [name]: value,
      }
    })

    if (name === 'hsnCode' && value) {
      setOtherChargesSacCode(value)
    }
    
    if (value && name) {
      dispatch({ type: 'SET', payload: { name: name, value: value } })
    } else {
      dispatch({ type: 'ERROR', payload: { name: name, value: value } })
    }
  }

  const equalWidthSx = singleRow ? { flex: 1, minWidth: 0 } : {}
  const chargeTitleMd = singleRow ? undefined : 5
  const amountMd = singleRow ? undefined : (isGSTEnabled ? 2.5 : 5)
  const weightRateMd = singleRow ? undefined : (isGSTEnabled ? 2.5 : 5)
  const addButtonMd = singleRow ? undefined : 2
  const addButtonSx = singleRow
    ? { flex: '0 0 auto', width: 90 }
    : {}

  const formContent = (
    <>
      <Grid item md={chargeTitleMd} sx={equalWidthSx}>
        <TextField
          size="small"
          inputRef={inputRef}
          name="chargeTitle"
          value={charge?.chargeTitle ? charge?.chargeTitle : ''}
          label="Charge Title"
          type="text"
          onChange={onInputChange}
          error={addOtherChargesData['chargeTitle']['error']}
          helperText={addOtherChargesData['chargeTitle']['helperText']}
          required
          fullWidth
        />
      </Grid>

      {enableWeightFields && (
        <>
          <Grid item md={weightRateMd} sx={equalWidthSx}>
            <CustomNumberField
              name="quantity"
              InputLabelProps={{ shrink: !!charge?.quantity }}
              value={charge?.quantity ?? ''}
              label="Weight"
              onKeyDown={handleKeyUp}
              onChange={onInputChange}
              error={addOtherChargesData['quantity']['error']}
              helperText={addOtherChargesData['quantity']['helperText']}
              required
              maxLength={8}
              fullWidth
            />
          </Grid>
          <Grid item md={weightRateMd} sx={equalWidthSx}>
            <CustomNumberField
              name="rate"
              InputLabelProps={{ shrink: !!charge?.rate }}
              value={charge?.rate ?? ''}
              label="Rate"
              onKeyDown={handleKeyUp}
              onChange={onInputChange}
              error={addOtherChargesData['rate']['error']}
              helperText={addOtherChargesData['rate']['helperText']}
              required
              maxLength={8}
              fullWidth
            />
          </Grid>
        </>
      )}

      {!enableWeightFields && (
        <Grid item md={amountMd} sx={equalWidthSx}>
          <CustomNumberField
            name="amount"
            InputLabelProps={{ shrink: !!charge?.amount }}
            value={charge?.amount ? charge?.amount : ''}
            label="Amount"
            onKeyDown={handleKeyUp}
            onChange={onInputChange}
            error={addOtherChargesData['amount']['error']}
            helperText={addOtherChargesData['amount']['helperText']}
            required
            maxLength={8}
            fullWidth
          />
        </Grid>
      )}

      {isGSTEnabled && (
        <Grid item md={singleRow ? undefined : 2.5} sx={singleRow ? { flex: '0 0 auto', width: 100 } : {}}>
          <TextField
            size="small"
            name="hsnCode"
            value={charge?.hsnCode ? charge?.hsnCode : ''}
            label="SAC Code"
            type="text"
            onChange={onInputChange}
            error={addOtherChargesData['hsnCode']['error']}
            helperText={addOtherChargesData['hsnCode']['helperText']}
            required
            inputProps={{ maxLength: 10 }}
            fullWidth
          />
        </Grid>
      )}

      <Grid item md={addButtonMd} sx={addButtonSx}>
          <Button
            id="id_button_add"
            sx={{
              height: 37,
              width: 90,
              backgroundColor: 'rgb(0, 128, 0, 0.5)',
              color: 'green',
              borderRadius: 1.2,
            }}
            size="large"
            onClick={adOtherChargesHandler}
          >
            ADD
          </Button>
      </Grid>
    </>
  )

  if (singleRow) {
    return <>{formContent}</>
  }

  return (
    <Grid container spacing={1.25} sx={{ mb: 4 }}>
      {formContent}
    </Grid>
  )
}
export default AddOtherCharges
